import React from "react";
import { Box, IconButton, Tooltip } from "@mui/material";
import CreateIcon from "@mui/icons-material/Create";
import DeleteIcon from "@mui/icons-material/Delete";
import { createSanitizedMarkup } from "../../utils/createSanitizedMarkup";
import { DataTable, TableColumnDef } from "../../components/DataTable";
import { TableThumbnail } from "../../components/TableThumbnail";
import type { ContentLanguage } from "../../hooks/useContentLanguage";

import type { ChallengeItem } from "./types";

type Props = {
  items: ChallengeItem[];
  onDelete: (id: string) => void;
  onEdit: (newsItem: ChallengeItem) => void;
  canEdit: boolean;
  canDelete: boolean;
  page: number;
  count: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (perPage: number) => void;
  lang: ContentLanguage;
};

export const ItemsList = ({ items, onDelete, onEdit, canEdit, canDelete, page, count, rowsPerPage, onPageChange, onRowsPerPageChange, lang }: Props) => {
  const columns: TableColumnDef<ChallengeItem>[] = [
    {
      header: "Bild",
      width: "130px",
      render: (item) => (
        <TableThumbnail src={lang === "de" ? item.image_ger : item.image} />
      ),
    },
    {
      header: "Name",
      render: (item) => (lang === "de" ? item.title_ger : item.title) || "-",
      getValue: (item) => (lang === "de" ? item.title_ger : item.title),
    },
    {
      header: "Beschreibung",
      render: (item) => {
        // Content is sanitized via createSanitizedMarkup (uses DOMPurify)
        const markup = createSanitizedMarkup({ html: (lang === "de" ? item.desc_ger : item.desc) || "" });
        return (
          <Box
            className="showmore-class"
            sx={{ p: 1 }}
            dangerouslySetInnerHTML={markup}
          />
        );
      },
    },
    {
      header: "Preis",
      render: (item) => item.price,
      getValue: (item) => item.price,
    },
    {
      header: "Einstellungen",
      render: (item) => (
        <Box sx={{ display: "flex", gap: 0.5 }}>
          {canEdit && (
            <Tooltip title="Bearbeiten" arrow>
              <IconButton size="small" color="primary" onClick={() => onEdit(item)}>
                <CreateIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {canDelete && (
            <Tooltip title="Löschen" arrow>
              <IconButton size="small" color="error" onClick={() => onDelete(item._id)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  return (
    <DataTable
      rows={items}
      columns={columns}
      page={page}
      count={count}
      rowsPerPage={rowsPerPage}
      onPageChange={onPageChange}
      onRowsPerPageChange={onRowsPerPageChange}
    />
  );
};
